// SWR
import useSWR from 'swr'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

/**
 * Cached read through the Laravel API — what call sites used to get by querying
 * Postgres straight from the browser.
 *
 * Returns `{ data, error, isLoading, mutate }`, the shape those call sites
 * already destructured. `data` is the response body, already unwrapped from
 * axios: an endpoint returning a JSON array gives an array here.
 *
 * Pass `null` as the endpoint to skip the request (SWR's conditional fetching),
 * which is how a select waits for the id it depends on.
 */
export default function useApiSWR(endpoint, params = null, options = {}) {
    const { requestGet } = useAxiosApi()

    // Params take part in the cache key: two callers asking the same endpoint
    // with different filters must not share an entry.
    const key = endpoint ? (params ? [endpoint, params] : endpoint) : null

    const { data, error, isLoading, mutate } = useSWR(
        key,
        async () => {
            const response = await requestGet(endpoint, params)

            return response.data
        },
        options
    )

    return { data, error, isLoading, mutate }
}

/**
 * Same thing, for lists that never change during a session (categories,
 * collections, locations, suppliers). Revalidating those on every window focus
 * costs a request per select on screen and never returns anything new.
 */
export function useApiSWRReference(endpoint, params = null, options = {}) {
    return useApiSWR(endpoint, params, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        ...options,
    })
}
